import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '@config/configuration.service';
import {
    PersonalDetails, ImageUploadResponse, PersonalDetailsUpdateResponse, MyPlanInfoResponse, ActiveSession,
    UpdateProfileResponse,
    VerifyInitialDetailsOtpResponse,
    ResendInitialDetailsOtpResponse,
    IsContactRegisteredResponse
} from '../../components/personal-details/interfaces/personal-details.interfaces';
import { tap, map, share } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { Utils } from '@app/_helpers';
import { EXECUTION_STYLE } from '@app/_shared/modules/strategy-form/strategy.enums';
import { AutoDeploymentStreamPayload } from '@app/_shared/providers/portfolio/portfolio.interface';
import { AuthService } from '@app/modules/auth';
import { AlgoPermissions, PlanType } from '../../../../../../../utProto/webEnums_pb';
import { PromotionPlan } from '@app/modules/admin/providers/plan-n-pricing/plan-n-pricing.interfaces';

@Injectable({
    providedIn: 'root'
})
export class PersonalDetailsService {
    personalDetails: PersonalDetails;
    $detailRequest: Observable<PersonalDetails>;
    $planiInfoRequest: Observable<MyPlanInfoResponse>;
    $activeSessionsRequest: Observable<ActiveSession[]>;
    planCache: MyPlanInfoResponse;
    autoDeployPortfoliosCache: any;
    portfolioIdFilled = new Subject<any>();
    autoDeployportfolios:Set<string> = new Set();
    isSubscribedPlanRestricted: boolean = false;
    isCarryForwardAllowed: boolean = false;
    isIntradayAllowed: boolean = false;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private configurationService: ConfigurationService,
    ) { }

    getAllAutoDeployPortfolios() {
        this.autoDeployportfolios = new Set();
        this.getAutoDeployPortfolios(true).subscribe((res) => {  
            res.forEach((exchange) => {
                exchange.portfolios.forEach((portfolio) => {
                    this.autoDeployportfolios.add(portfolio.originalPortfolioID)
                })    
            })      
            this.portfolioIdFilled.next(this.autoDeployportfolios);       
        })
    }

    /**
     * Updates the auto deploy portfolios cache with the given auto deployment stream payload.
     * If marketID exists in the cache, it updates the portfolios array with the given auto deployment details.
     * If autoDeployPortfolio is true, it adds the auto deployment details to the portfolios array.
     * If autoDeployPortfolio is false, it removes the auto deployment details from the portfolios array.
     * If marketID does not exist in the cache, it adds a new entry with the given marketID and auto deployment details.
     * @param autoDeploymentStreamPayload - The auto deployment stream payload containing the market ID and auto deployment details.
     */
    updateAutoDeployPortfoliosCache(autoDeploymentStreamPayload: AutoDeploymentStreamPayload) {
        const portfolios = this.autoDeployPortfoliosCache?.find((exchangePortfolio) => exchangePortfolio.marketID == autoDeploymentStreamPayload.market_id)?.portfolios;
        if (portfolios) {
            const autoDeploymentDetails = autoDeploymentStreamPayload.autoDeploymentDetails;
            const portfolioIndex = portfolios.findIndex((portfolio) => portfolio.portfolioID == autoDeploymentDetails.portfolioID);
            if (autoDeploymentDetails.autoDeployPortfolio) {
                if (portfolioIndex >= 0) {
                    portfolios[portfolioIndex] = autoDeploymentDetails;
                } else {
                    portfolios.push(autoDeploymentDetails);
                }
            } else {
                if (portfolioIndex >= 0) {
                    portfolios.splice(portfolioIndex, 1);
                }
            }
        } else {
            this.autoDeployPortfoliosCache?.push({
                clientID: this.authService.accessTokenObj.clientId,
                marketID: autoDeploymentStreamPayload.market_id,
                portfolios: [autoDeploymentStreamPayload.autoDeploymentDetails]
            })
        }
    }

    getPersonalDetails(update: boolean = false) {
        const url = this.configurationService.applicationUrls.details;
        if (this.personalDetails && !update) {
            return of(this.personalDetails);
        }

        if (this.$detailRequest) { return this.$detailRequest; }

        this.$detailRequest = this.http.get<PersonalDetails>(url).pipe(
            share(),
            map(response => {
                if (response) {
                    response['dob'] = response['dob'] ? <any>Utils.stringToEpoch(response['dob']) * 1e3 : null;

                    if (!(response instanceof PersonalDetails)) {
                        response = new PersonalDetails(response);
                    }

                    this.personalDetails = response;
                    this.$detailRequest = null;
                    return this.personalDetails;
                }
            })
        );

        return this.$detailRequest;
    }

    updatePersonalDetails(detailsObj: PersonalDetails) {
        const url = this.configurationService.applicationUrls.updateDetails;
        return this.http.put<PersonalDetailsUpdateResponse>(url, detailsObj).pipe(
            map(response => {
                if (this.personalDetails && response) {

                    Object.assign(this.personalDetails, response);
                    this.personalDetails.dob = this.personalDetails.dob ? response.dob * 1e3 : null;
                    return this.personalDetails;

                }

            })
        );
    }


    uploadProfileImage(imageBase64: string) {
        const url = this.configurationService.applicationUrls.picture;
        return this.http.put<ImageUploadResponse>(
            url, {
            'image': imageBase64
        }).pipe(
            tap(response => {
                if (this.personalDetails) {
                    const data = this.personalDetails;
                    data.image = response.Image;
                }
            })
        );
    }

    getPlanInfo(update: boolean = false) {
        if (this.planCache && !update) {
            return of(this.planCache);
        }
        if (this.$planiInfoRequest) { return this.$planiInfoRequest; }

        this.$planiInfoRequest = this.http.get<MyPlanInfoResponse>(this.configurationService.applicationUrls.planInfo).pipe(
            share(),
            map((plan) => {
                plan.plan.brokers = plan.plan.broker.split(',');
                this.isSubscribedPlanRestricted = (plan.plan_type == PlanType.PLANTYPE_ONETIME);
                plan.plan.allowed_strategies = plan.plan.allowed_strategies || [];
                for(let permission of plan.plan.algo_permissions){
                    if(permission === AlgoPermissions.WEBALGOPERMISSION_INTRADAY){
                        this.isIntradayAllowed = true;
                    } else if(permission === AlgoPermissions.WEBALGOPERMISSION_CARRYFORWARD){
                        this.isCarryForwardAllowed = true;
                    }
                    if(this.isCarryForwardAllowed && this.isIntradayAllowed){
                        break;
                    }
                }
                
                this.planCache = plan;
                return plan
            })
        );
        return this.$planiInfoRequest
    }

    listPromotionPlans(){
        return this.http.get<PromotionPlan[]>(this.configurationService.applicationUrls.listPromotionPlans);
    }

    updateBrokerPlanFailurePopup(flag: boolean) {
        return this.http.put<any>(this.configurationService.applicationUrls.brokerPlanFailure, { insufficient_balance: flag });
    }

    getAutoDeployPortfolios(update: boolean = false) {
        if (this.autoDeployPortfoliosCache && !update) {
            return of(this.autoDeployPortfoliosCache);
        }
        const portfolios = this.http.get<any>(this.configurationService.applicationUrls.autoDeployPortfolios).pipe(
            share(),
            map((portfolio) => {
                this.autoDeployPortfoliosCache = portfolio
                return portfolio
            })
        );
        return portfolios;
    }

    isModeAllowed(mode: EXECUTION_STYLE) {
        // UnUsed
        return this.getPlanInfo().pipe(
            map(plan => {
                return false;
            })
        )

    }

    getActiveSessions(period: number) {
        const url = this.configurationService.applicationUrls.activeSessions;
        this.$activeSessionsRequest = this.http.get<ActiveSession[]>(url, {
            params: {
                sessionPeriod: period
            } as any
        });
        return this.$activeSessionsRequest;
    }

    isEmailRegistered(email: string) {
        const url = this.configurationService.applicationUrls.isEmailRegistered;
        return this.http.post<IsContactRegisteredResponse>(url, { email });
    }

    isPhoneRegistered(mobile_number: string) {
        const url = this.configurationService.applicationUrls.isPhoneRegistered;
        return this.http.post<IsContactRegisteredResponse>(url, { mobile_number });
    }

    updateProfileContact(data: {name?: string, email?: string, phone?: string}) {
        const url = this.configurationService.applicationUrls.updateUserDetails;
        return this.http.post<UpdateProfileResponse>(url, data);
    }

    verifyInitialDetailsOtp(payload: { email?: string, phone?: string, otp: string, session_id?: string }) {
        const url = this.configurationService.applicationUrls.verifyInitialDetailsEmailOtp;
        return this.http.post<VerifyInitialDetailsOtpResponse>(url, payload);
    }

    resendInitialDetailsOtp(payload: { email?: string, mobile_number?: string, session_id?: string }) {
        const url = this.configurationService.applicationUrls.resendInitialDetailsOtp;
        return this.http.post<ResendInitialDetailsOtpResponse>(url, payload);
    }

    get isSharingAllowedInPlan() {
        if (this.planCache) {
            return this.planCache.is_algo_provider
        }
        return false
    }
}
